import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CollapseHeader from '../../../core/common/collapse-header';
import { all_routes } from '../../router/all_routes';
import { checkPermission } from '../../../core/permissons/CheckPermissons';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import apiDefinitions from '../../../core/api/apiDefinitions';
import ViewVehicleBookings from './VehicleBooking/ViewVehicleBookings';
import EditVehicleBookings from './VehicleBooking/EditVehicleBookings';
import PendingBookingsModal from './VehicleBooking/PendingBookingsModal';
import ApprovedBookingsModal from './VehicleBooking/ApprovedBookingsModal';
import FinalApprovedBookingsModal from './VehicleBooking/FinalApprovedBookingsModal';
import CompletedBookingsModal from './VehicleBooking/CompletedBookingsModal';
import OngoingBookingsModal from './VehicleBooking/OngoingBookingsModal';
import RecurringBookingsModal from './VehicleBooking/RecurringBookingsModal';
import RejectedBookingsModal from './VehicleBooking/RejectedBookingsModal';
import MergeRequestModal from './VehicleBooking/MergeRequestModal';
import MergedBookingsModal from './VehicleBooking/MergedBookingsModal';
import ApprovedModal from './VehicleBooking/ApprovedModal';
import DirectGatePassModal from './VehicleBooking/DirectGatePassModal';
import Swal from 'sweetalert2';

export interface Location {
  id: number;
  orderId: number;
  address: string;
  lat: string;
  lng: string;
  status: string | null;
  createdAt: string | null;
  type: 'start' | 'stop' | 'return';
}

export interface User {
  empNumber: string;
  name: string;
  email: string;
  status: string | null;
  phone: string | null;
  createdBy: string | null;
  createdAt: string | null;
  group: string | null;
  departmentAdminSpecificData: any | null;
  driverSpecificData: any | null;
}

export interface Booking {
  id: number;
  vehicle: any | null;
  user: User;
  reason: string;
  intermediateApprovedBy: any | null;
  intermediateApprovedDate: string | null;
  comment: string | null;
  noOfPassengers: number;
  departureTime: number;
  arrivalTime: number;
  outTime: string | null;
  mileageOut: number;
  outDriverId: string | null;
  inTime: string | null;
  outConfirmedBy: string | null;
  outComment: string | null;
  mileageIn: number;
  inDriverId: string | null;
  inConfirmedBy: string | null;
  inComment: string | null;
  estimatedKms: number;
  status: string;
  driver: any | null;
  driverId?: string | null;
  vehicleId?: string | null;
  bookingType: string;
  priority: string;
  recurringVehicleBooking: any | null;
  locations: Location[];
  finalApprovedBy: string | null;
  finalApprovedDate: string | null;
  finalComment: string | null;
  createdBy: string;
  createdAt: string;
  suggestedDepartureTime?: number | string | null;
  suggestedArrivalTime?: string | null;
  totalWaitingTime: number;
  groupId?: string | null;
}

interface RecurringBooking {
  id: number;
  vehicleBookingRequestId: number;
  reason: string;
  noOfPassengers: number;
  departureTime: number;
  frequency: string;
  endDate: number;
  status: string;
  createdBy: string;
  createdAt: number;
}

export interface MergeSuggestion {
  groupId: string;
  bookings: Booking[];
  vehicleSuggestions: any[];
  driverSuggestions: any[];
  suggestedDepartureTime: string | null;
  suggestedArrivalTime: string | null;
  comment: string | null;
}

export interface MergedGroup {
  groupId: string;
  bookings: Booking[];
  suggestedDepartureTime: string;
  suggestedArrivalTime?: string | null;
  vehicleSuggestions?: Array<{
    numberPlate: string;
    noOfPassengers: number;
  }>;
  driverSuggestions?: Array<{
    empNumber: string;
    empName: string;
    licenseType?: string | null;
    licenseNumber?: string | null;
    expireDate?: string | null;
  }>;
  comment?: string | null;
  mergeGroupLocation?: Array<{
    id: number;
    address: string;
    lat: string;
    lng: string;
    orderId: number;
    createdAt: string;
    createdBy: string;
  }>;
}

interface ApiResponse {
  status: string;
  statusDescription: string;
  data: {
    vehicleBookingRequests: Booking[];
    total: number;
  };
}

interface RecurringApiResponse {
  status: string;
  statusDescription: string;
  data: {
    bookings: RecurringBooking[];
    total: number;
  };
}

interface MergeApiResponse {
  status: string;
  statusDescription: string;
  data: MergeSuggestion[];
}

interface MergedApiResponse {
  status: string;
  statusDescription: string;
  data: MergedGroup[];
}

const route = all_routes;

const Booking: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  const [pendingData, setPendingData] = useState<Booking[]>([]);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [isPendingLoading, setIsPendingLoading] = useState(false);
  const [pendingTotal, setPendingTotal] = useState<number>(0);

  const [approvedData, setApprovedData] = useState<Booking[]>([]);
  const [approvedError, setApprovedError] = useState<string | null>(null);
  const [isApprovedLoading, setIsApprovedLoading] = useState(false);
  const [approvedTotal, setApprovedTotal] = useState<number>(0);

  const [newApprovedData, setNewApprovedData] = useState<Booking[]>([]);
  const [newApprovedError, setNewApprovedError] = useState<string | null>(null);
  const [isNewApprovedLoading, setIsNewApprovedLoading] = useState(false);
  const [newApprovedTotal, setNewApprovedTotal] = useState<number>(0);

  const [finalApprovedData, setFinalApprovedData] = useState<Booking[]>([]);
  const [finalApprovedError, setFinalApprovedError] = useState<string | null>(null);
  const [isFinalApprovedLoading, setIsFinalApprovedLoading] = useState(false);
  const [finalApprovedTotal, setFinalApprovedTotal] = useState<number>(0);

  const [completedData, setCompletedData] = useState<Booking[]>([]);
  const [completedError, setCompletedError] = useState<string | null>(null);
  const [isCompletedLoading, setIsCompletedLoading] = useState(false);
  const [completedTotal, setCompletedTotal] = useState<number>(0);

  const [rejectedData, setRejectedData] = useState<Booking[]>([]);
  const [rejectedError, setRejectedError] = useState<string | null>(null);
  const [isRejectedLoading, setIsRejectedLoading] = useState(false);
  const [rejectedTotal, setRejectedTotal] = useState<number>(0);

  const [OngoingBookingsData, setOngoingBookingsData] = useState<Booking[]>([]);
  const [OngoingBookingsError, setOngoingBookingsError] = useState<string | null>(null);
  const [isOngoingBookingsLoading, setIsOngoingBookingsLoading] = useState(false);
  const [OngoingBookingsTotal, setOngoingBookingsTotal] = useState<number>(0);

  const [recurringData, setRecurringData] = useState<RecurringBooking[]>([]);
  const [recurringError, setRecurringError] = useState<string | null>(null);
  const [isRecurringLoading, setIsRecurringLoading] = useState(false);
  const [recurringTotal, setRecurringTotal] = useState<number>(0);

  const [mergeSuggestions, setMergeSuggestions] = useState<MergeSuggestion[]>([]);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [isMergeLoading, setIsMergeLoading] = useState(false);
  const [mergeTotal, setMergeTotal] = useState<number>(0);

  const [mergedGroups, setMergedGroups] = useState<MergedGroup[]>([]);
  const [mergedError, setMergedError] = useState<string | null>(null);
  const [isMergedLoading, setIsMergedLoading] = useState<boolean>(false);
  const [mergedTotal, setMergedTotal] = useState<number>(0);
  const [mergedFilter, setMergedFilter] = useState<string>('ACCEPTED');

  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingDetails, setPendingDetails] = useState<Booking[]>([]);
  const [pendingModalError, setPendingModalError] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [approvedDetails, setApprovedDetails] = useState<Booking[]>([]);
  const [approvedModalError, setApprovedModalError] = useState<string | null>(null);

  const [showNewApprovedModal, setShowNewApprovedModal] = useState(false);
  const [newApprovedDetails, setNewApprovedDetails] = useState<Booking[]>([]);
  const [newApprovedModalError, setNewApprovedModalError] = useState<string | null>(null);

  const [showFinalApprovedModal, setShowFinalApprovedModal] = useState(false);
  const [finalApprovedDetails, setFinalApprovedDetails] = useState<Booking[]>([]);
  const [finalApprovedModalError, setFinalApprovedModalError] = useState<string | null>(null);

  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [completedDetails, setCompletedDetails] = useState<Booking[]>([]);
  const [completedModalError, setCompletedModalError] = useState<string | null>(null);

  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [rejectedDetails, setRejectedDetails] = useState<Booking[]>([]);
  const [rejectedModalError, setRejectedModalError] = useState<string | null>(null);

  const [showOngoingBookingsModal, setShowOngoingBookingsModal] = useState(false);
  const [OngoingBookingsDetails, setOngoingBookingsDetails] = useState<Booking[]>([]);
  const [OngoingBookingsModalError, setOngoingBookingsModalError] = useState<string | null>(null);

  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringDetails, setRecurringDetails] = useState<RecurringBooking[]>([]);
  const [recurringModalError, setRecurringModalError] = useState<string | null>(null);

  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeDetails, setMergeDetails] = useState<MergeSuggestion[]>([]);
  const [mergeModalError, setMergeModalError] = useState<string | null>(null);

  const [showMergedModal, setShowMergedModal] = useState(false);
  const [mergedDetails, setMergedDetails] = useState<MergedGroup[]>([]);
  const [mergedModalError, setMergedModalError] = useState<string | null>(null);

  const [showGatePassModal, setShowGatePassModal] = useState<boolean>(false);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const [isMerging, setIsMerging] = useState<boolean>(false);
  const [mergeActionError, setMergeActionError] = useState<string | null>(null);

  const pageSize = 16;

  const navItems1 = [
    { label: 'Booking Dashboard', icon: 'ti ti-dashboard', link: route.booking, permission: 'ROLE_ADD_VEHICLE_BOOKING' },
    { label: 'All Bookings', icon: 'ti ti-plus', link: route.allBookings, permission: 'ROLE_VIEW_ALL_BOOKINGS' },
  ];

  const statusPermissions: { [key: string]: string } = {
    PENDING: 'ROLE_PENDING_VEHICLE_BOOKING',
    APPROVED: 'ROLE_FINAL_APPROVE_VEHICLE_BOOKING',
    FINAL_APPROVED_NEW: 'ROLE_PENDING_VEHICLE_BOOKING',
    FINAL_APPROVED: 'ROLE_FINAL_APPROVE_VEHICLE_BOOKING',
    COMPLETED: 'ROLE_COMPLETED_VEHICLE_BOOKING',
    REJECTED: 'ROLE_VIEW_REJECTED_BOOKINGS',
    OngoingBookings: 'ROLE_VIEW_REJECTED_BOOKINGS',
    RECURRING: 'ROLE_GET_RECURRING_VEHICLE_BOOKING_REQUESTS',
    MERGE_SUGGESTIONS: 'ROLE_GET_SUGGESTIONS_FOR_MERGE_VEHICLE_BOOKING',
    MERGED_BOOKINGS: 'ROLE_GET_MERGE_GROUPS',
  };

 
  const statusColors: { [key: string]: string } = {
    PENDING: 'badge bg-secondary',
    APPROVED: 'badge bg-success',
    ONGOING:'badge bg-info',
    FINAL_APPROVED_NEW: 'badge bg-info',
    FINAL_REJECTED: 'badge bg-primary',
    FINAL_APPROVED: 'badge bg-success',
    COMPLETED: 'badge bg-success-gradient',
    CANCEL: 'badge bg-danger',
    REJECTED: 'badge bg-danger',
    CANCELLED: 'badge bg-danger',
    MERGED: 'badge bg-teal',
    EXPIRED: 'badge bg-danger',
  };
  const statusCardStyles: { [key: string]: { bgColor: string; iconColor: string; icon: string; className: string } } = {
    PENDING: { bgColor: '#fef3c7', iconColor: '#f59e0b', icon: 'fas fa-clock', className: 'bg-amber-100' },
    FINAL_APPROVED: { bgColor: '#d1fae5', iconColor: '#10b981', icon: 'fas fa-check-circle', className: 'bg-green-100' },
    APPROVED: { bgColor: '#e0f2fe', iconColor: '#3b82f6', icon: 'fas fa-check', className: 'bg-blue-100' },
    FINAL_APPROVED_NEW: { bgColor: '#e0f2fe', iconColor: '#3b82f6', icon: 'fas fa-check-double', className: 'bg-blue-100' },
    COMPLETED: { bgColor: '#dbeafe', iconColor: '#2563eb', icon: 'fas fa-flag-checkered', className: 'bg-blue-100' },
    REJECTED: { bgColor: '#fee2e2', iconColor: '#ef4444', icon: 'fas fa-times-circle', className: 'bg-red-100' },
   OngoingBookings: { bgColor: '#f3e8ff', iconColor: '#9333ea', icon: 'fas fa-hourglass-half',className: 'bg-purple-100' },
    RECURRING: { bgColor: '#f3e8ff', iconColor: '#7c3aed', icon: 'fas fa-redo', className: 'bg-violet-100' },
    MERGE_SUGGESTIONS: { bgColor: '#ccfbf1', iconColor: '#14b8a6', icon: 'fas fa-link', className: 'bg-teal-100' },
    MERGED_BOOKINGS: { bgColor: '#ccfbf1', iconColor: '#14b8a6', icon: 'fas fa-object-group', className: 'bg-teal-100' },
  };

  const statusLabels: { [key: string]: string } = {
    PENDING: 'Pending Approval',
    APPROVED: 'Final Approved Bookings',
    FINAL_APPROVED_NEW: 'Approved Bookings',
    FINAL_APPROVED: 'Pending Final Approved',
    COMPLETED: 'Completed Bookings',
    REJECTED: 'Rejected Bookings',
    OngoingBookings: 'Ongoing Bookings',
    RECURRING: 'Recurring Bookings',
    MERGE_SUGGESTIONS: 'Merge Suggestions',
    MERGED_BOOKINGS: 'Merged Bookings',
  };

  const defaultGetByPatternRequest = {
    filter: {},
  };

  const canUpdateBooking = (booking: Booking): boolean => {
    const isCreator = booking.createdBy === currentUser.empNumber;
    const isServiceManager = localStorage.group.includes('SERVICE_MANAGER');
    const isDepartmentHead = localStorage.group.includes('DEPARTMENT_HEAD');
    const bookingStatus = booking.status?.toUpperCase() || 'UNKNOWN';



    if (['COMPLETED', 'MERGED', 'EXPIRED'].includes(bookingStatus)) {
      return false;
    }

    if (isServiceManager && ['PENDING', 'APPROVED', 'FINAL_APPROVED'].includes(bookingStatus)) {
      return true;
    }

    if (isDepartmentHead && ['PENDING', 'APPROVED'].includes(bookingStatus)) {
      return true;
    }

    if (isCreator && bookingStatus === 'PENDING') {
      return true;
    }

    return false;
  };

  const currentUser = {
    empNumber: localStorage.getItem('username') || localStorage.getItem('empNumber') || 'unknown',
    roles: JSON.parse(localStorage.getItem('roles') || '[]'),
    group: localStorage.getItem('storedGroup') || null,
  };

  const isServiceManager = currentUser.roles.includes('ROLE_SERVICE_MANAGER') || currentUser.group === 'SERVICE_MANAGER';


  // Helper function to show loading toast and return toast ID
  const showLoadingToast = (message: string) => {
    return toast.loading(message, {
      position: 'top-right',
      autoClose: false,
    });
  };

  // Helper function to update toast on success or error
  const updateToast = (toastId: number | string, message: string, type: 'success' | 'error') => {
    toast.update(toastId, {
      render: message,
      type,
      isLoading: false,
      autoClose: 3000,
      hideProgressBar: false,
    });
  };

  // make this helper tolerant to different shapes from backend
const extractListAndTotal = (data: any) => {
  const list =
    data?.vehicleBookingRequests ??
    data?.bookings ??
    Array.isArray(data) ? data : [];

  const total =
    typeof data?.total === 'number'
      ? data.total
      : Array.isArray(list)
      ? list.length
      : 0;

  return { list, total };
};

const fetchOngoingBookingsData = async () => {
  if (isOngoingBookingsLoading) return;
  const toastId = showLoadingToast('Fetching ongoing bookings...');
  setIsOngoingBookingsLoading(true);
  try {
    // ✅ use the ongoing API
    const response = await apiDefinitions.getOngoingVehicleBookings(1);
    const responseData = response.data as ApiResponse;

    if (responseData?.status === 'S1000') {
      const { list, total } = extractListAndTotal(responseData.data);
      setOngoingBookingsData(list as Booking[]);
      setOngoingBookingsTotal(total);
      setOngoingBookingsError(null);
      updateToast(toastId, 'Ongoing bookings fetched successfully!', 'success');
    } else {
      setOngoingBookingsData([]);
      setOngoingBookingsTotal(0);
      setOngoingBookingsError(responseData?.statusDescription || 'No ongoing bookings.');
      updateToast(toastId, responseData?.statusDescription || 'No ongoing bookings.', 'error');
    }
  } catch (error: any) {
    const msg = error?.response?.data?.statusDescription || 'Failed to fetch ongoing bookings.';
    setOngoingBookingsData([]);
    setOngoingBookingsTotal(0);
    setOngoingBookingsError(msg);
    updateToast(toastId, msg, 'error');
  } finally {
    setIsOngoingBookingsLoading(false);
  }
};

const loadOngoingBookingsDetails = async () => {
  const toastId = showLoadingToast('Loading ongoing booking details...');
  try {
    const response = await apiDefinitions.getOngoingVehicleBookings(1); // ✅
    const responseData = response.data as ApiResponse;

    if (responseData?.status === 'S1000') {
      const { list } = extractListAndTotal(responseData.data);
      setOngoingBookingsDetails(list as Booking[]);
      setOngoingBookingsModalError(null);
      updateToast(toastId, 'Ongoing booking details loaded successfully!', 'success');
    } else {
      setOngoingBookingsDetails([]);
      setOngoingBookingsModalError(responseData?.statusDescription || 'Failed to retrieve ongoing booking details.');
      updateToast(toastId, responseData?.statusDescription || 'Failed to retrieve ongoing booking details.', 'error');
    }
  } catch (error: any) {
    const msg = error?.response?.data?.statusDescription || 'Failed to fetch ongoing booking details.';
    setOngoingBookingsDetails([]);
    setOngoingBookingsModalError(msg);
    updateToast(toastId, msg, 'error');
  }
};


  const fetchOngoingBookingsDetails = async () => {
    await loadOngoingBookingsDetails();
    setShowOngoingBookingsModal(true);
  };

  const fetchPendingData = async () => {
    if (isPendingLoading) return;
    const toastId = showLoadingToast('Fetching pending bookings...');
    setIsPendingLoading(true);
    try {
      const response = await apiDefinitions.getPendingApprovalBookings(1);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000' && responseData.data.vehicleBookingRequests.length > 0) {
        setPendingData(responseData.data.vehicleBookingRequests.filter((b: Booking) => b.status === 'PENDING'));
        setPendingTotal(responseData.data.total || responseData.data.vehicleBookingRequests.length);
        setPendingError(null);
        updateToast(toastId, 'Pending bookings fetched successfully!', 'success');
      } else {
        setPendingData([]);
        setPendingTotal(0);
        setPendingError('No pending bookings available.');
        updateToast(toastId, 'No pending bookings available.', 'error');
      }
    } catch (error: any) {
      setPendingData([]);
      setPendingTotal(0);
      setPendingError(error.response?.data?.statusDescription || 'Failed to fetch pending bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch pending bookings.', 'error');
    } finally {
      setIsPendingLoading(false);
    }
  };

  const loadPendingDetails = async () => {
    const toastId = showLoadingToast('Loading pending booking details...');
    try {
      const countResponse = await apiDefinitions.getPendingApprovalBookings(1);
      const countData = countResponse.data as ApiResponse;
      let pageNumber = 1;

      if (countData.status === 'S1000') {
        const total = countData.data.total || countData.data.vehicleBookingRequests.length;
        pageNumber = Math.ceil(total / pageSize);
      } else {
        setPendingDetails([]);
        setPendingModalError(countData.statusDescription || 'Failed to retrieve pending booking details.');
        updateToast(toastId, countData.statusDescription || 'Failed to retrieve pending booking details.', 'error');
        return;
      }

      const response = await apiDefinitions.getPendingApprovalBookings(pageNumber);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000') {
        setPendingDetails(responseData.data.vehicleBookingRequests.filter((b: Booking) => b.status === 'PENDING'));
        setPendingModalError(null);
        updateToast(toastId, 'Pending booking details loaded successfully!', 'success');
      } else {
        setPendingDetails([]);
        setPendingModalError(responseData.statusDescription || 'Failed to retrieve pending booking details.');
        updateToast(toastId, responseData.statusDescription || 'Failed to retrieve pending booking details.', 'error');
      }
    } catch (error: any) {
      setPendingDetails([]);
      setPendingModalError(error.response?.data?.statusDescription || 'Failed to fetch pending booking details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch pending booking details.', 'error');
    }
  };

  const fetchPendingDetails = async () => {
    await loadPendingDetails();
    setShowPendingModal(true);
  };

  const fetchApprovedData = async () => {
    if (isApprovedLoading) return;
    const toastId = showLoadingToast('Fetching approved bookings...');
    setIsApprovedLoading(true);
    try {
      const response = await apiDefinitions.finalApproved({ status: 'FINAL_APPROVED' });
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000' && responseData.data.vehicleBookingRequests.length > 0) {
        setApprovedData(responseData.data.vehicleBookingRequests.filter((b: Booking) => b.status === 'APPROVED'));
        setApprovedTotal(responseData.data.total || responseData.data.vehicleBookingRequests.length);
        setApprovedError(null);
        updateToast(toastId, 'Approved bookings fetched successfully!', 'success');
      } else {
        setApprovedData([]);
        setApprovedTotal(0);
        setApprovedError('No approved bookings available.');
        updateToast(toastId, 'No approved bookings available.', 'error');
      }
    } catch (error: any) {
      setApprovedData([]);
      setApprovedTotal(0);
      setApprovedError(error.response?.data?.statusDescription || 'Failed to fetch approved bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch approved bookings.', 'error');
    } finally {
      setIsApprovedLoading(false);
    }
  };

  const loadApprovedDetails = async () => {
    const toastId = showLoadingToast('Loading approved booking details...');
    try {
      const response = await apiDefinitions.finalApproved({ status: 'FINAL_APPROVED' });
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000') {
        setApprovedDetails(responseData.data.vehicleBookingRequests.filter((b: Booking) => b.status === 'FINAL_APPROVED'));
        setApprovedModalError(null);
        updateToast(toastId, 'Approved booking details loaded successfully!', 'success');
      } else {
        setApprovedDetails([]);
        setApprovedModalError(responseData.statusDescription || 'Failed to retrieve approved booking details.');
        updateToast(toastId, responseData.statusDescription || 'Failed to retrieve approved booking details.', 'error');
      }
    } catch (error: any) {
      setApprovedDetails([]);
      setApprovedModalError(error.response?.data?.statusDescription || 'Failed to fetch approved booking details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch approved booking details.', 'error');
    }
  };

  const fetchApprovedDetails = async () => {
    await loadApprovedDetails();
    setShowApprovedModal(true);
  };

  const fetchNewApprovedData = async () => {
    if (isNewApprovedLoading) return;
    const toastId = showLoadingToast('Fetching new approved bookings...');
    setIsNewApprovedLoading(true);
    try {
      const response = await apiDefinitions.getApprovedBookings(1);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000' && responseData.data.vehicleBookingRequests.length > 0) {
        setNewApprovedData(responseData.data.vehicleBookingRequests.filter((b: Booking) => b.status === 'APPROVED'));
        setNewApprovedTotal(responseData.data.total || responseData.data.vehicleBookingRequests.length);
        setNewApprovedError(null);
        updateToast(toastId, 'New approved bookings fetched successfully!', 'success');
      } else {
        setNewApprovedData([]);
        setNewApprovedTotal(0);
        setNewApprovedError('No approved bookings available.');
        updateToast(toastId, 'No approved bookings available.', 'error');
      }
    } catch (error: any) {
      setNewApprovedData([]);
      setNewApprovedTotal(0);
      setNewApprovedError(error.response?.data?.statusDescription || 'Failed to fetch approved bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch approved bookings.', 'error');
    } finally {
      setIsNewApprovedLoading(false);
    }
  };

  const loadNewApprovedDetails = async () => {
    const toastId = showLoadingToast('Loading new approved booking details...');
    try {
      const response = await apiDefinitions.getApprovedBookings(1);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000') {
        setNewApprovedDetails(responseData.data.vehicleBookingRequests.filter((b: Booking) => b.status === 'APPROVED'));
        setNewApprovedModalError(null);
        updateToast(toastId, 'New approved booking details loaded successfully!', 'success');
      } else {
        setNewApprovedDetails([]);
        setNewApprovedModalError(responseData.statusDescription || 'Failed to retrieve approved booking details.');
        updateToast(toastId, responseData.statusDescription || 'Failed to retrieve approved booking details.', 'error');
      }
    } catch (error: any) {
      setNewApprovedDetails([]);
      setNewApprovedModalError(error.response?.data?.statusDescription || 'Failed to fetch approved booking details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch approved booking details.', 'error');
    }
  };

  const fetchNewApprovedDetails = async () => {
    await loadNewApprovedDetails();
    setShowNewApprovedModal(true);
  };

  const fetchFinalApprovedData = async () => {
    if (isFinalApprovedLoading) return;
    const toastId = showLoadingToast('Fetching final approved bookings...');
    setIsFinalApprovedLoading(true);
    try {
      const response = await apiDefinitions.getPendingFinalApprovalBookings(1);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000' && responseData.data.vehicleBookingRequests.length > 0) {
        setFinalApprovedData(responseData.data.vehicleBookingRequests);
        setFinalApprovedTotal(responseData.data.total || responseData.data.vehicleBookingRequests.length);
        setFinalApprovedError(null);
        updateToast(toastId, 'Final approved bookings fetched successfully!', 'success');
      } else {
        setFinalApprovedData([]);
        setFinalApprovedTotal(0);
        setFinalApprovedError('No final approved bookings available.');
        updateToast(toastId, 'No final approved bookings available.', 'error');
      }
    } catch (error: any) {
      setFinalApprovedData([]);
      setFinalApprovedTotal(0);
      setFinalApprovedError(error.response?.data?.statusDescription || 'Failed to fetch final approved bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch final approved bookings.', 'error');
    } finally {
      setIsFinalApprovedLoading(false);
    }
  };

  const loadFinalApprovedDetails = async () => {
    const toastId = showLoadingToast('Loading final approved booking details...');
    try {
      const response = await apiDefinitions.getPendingFinalApprovalBookings(1);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000') {
        setFinalApprovedDetails(responseData.data.vehicleBookingRequests);
        setFinalApprovedModalError(null);
        updateToast(toastId, 'Final approved booking details loaded successfully!', 'success');
      } else {
        setFinalApprovedDetails([]);
        setFinalApprovedModalError(responseData.statusDescription || 'Failed to retrieve final approved booking details.');
        updateToast(toastId, responseData.statusDescription || 'Failed to retrieve final approved booking details.', 'error');
      }
    } catch (error: any) {
      setFinalApprovedDetails([]);
      setFinalApprovedModalError(error.response?.data?.statusDescription || 'Failed to fetch final approved booking details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch final approved booking details.', 'error');
    }
  };

  const fetchFinalApprovedDetails = async () => {
    await loadFinalApprovedDetails();
    setShowFinalApprovedModal(true);
  };

  const fetchCompletedData = async () => {
    if (isCompletedLoading) return;
    const toastId = showLoadingToast('Fetching completed bookings...');
    setIsCompletedLoading(true);
    try {
      const response = await apiDefinitions.getCompletedBookings(1);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000' && responseData.data.vehicleBookingRequests.length > 0) {
        setCompletedData(responseData.data.vehicleBookingRequests);
        setCompletedTotal(responseData.data.total || responseData.data.vehicleBookingRequests.length);
        setCompletedError(null);
        updateToast(toastId, 'Completed bookings fetched successfully!', 'success');
      } else {
        setCompletedData([]);
        setCompletedTotal(0);
        setCompletedError('No completed bookings available.');
        updateToast(toastId, 'No completed bookings available.', 'error');
      }
    } catch (error: any) {
      setCompletedData([]);
      setCompletedTotal(0);
      setCompletedError(error.response?.data?.statusDescription || 'Failed to fetch completed bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch completed bookings.', 'error');
    } finally {
      setIsCompletedLoading(false);
    }
  };

  const loadCompletedDetails = async () => {
    const toastId = showLoadingToast('Loading completed booking details...');
    try {
      const response = await apiDefinitions.getCompletedBookings(1);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000') {
        setCompletedDetails(responseData.data.vehicleBookingRequests);
        setCompletedModalError(null);
        updateToast(toastId, 'Completed booking details loaded successfully!', 'success');
      } else {
        setCompletedDetails([]);
        setCompletedModalError(responseData.statusDescription || 'Failed to retrieve completed booking details.');
        updateToast(toastId, responseData.statusDescription || 'Failed to retrieve completed booking details.', 'error');
      }
    } catch (error: any) {
      setCompletedDetails([]);
      setCompletedModalError(error.response?.data?.statusDescription || 'Failed to fetch completed booking details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch completed booking details.', 'error');
    }
  };

  const fetchCompletedDetails = async () => {
    await loadCompletedDetails();
    setShowCompletedModal(true);
  };

  const fetchRejectedData = async () => {
    if (isRejectedLoading) return;
    const toastId = showLoadingToast('Fetching rejected bookings...');
    setIsRejectedLoading(true);
    try {
      const response = await apiDefinitions.getRejectedBookings(1);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000' && responseData.data.vehicleBookingRequests) {
        const rejected = responseData.data.vehicleBookingRequests.filter(
          (b: Booking) => b.status === 'FINAL_REJECTED'
        );
        setRejectedData(responseData.data.vehicleBookingRequests);
        setRejectedTotal(responseData.data.total || responseData.data.vehicleBookingRequests.length);
        setRejectedError(null);
        updateToast(toastId, 'Rejected bookings fetched successfully!', 'success');
      } else {
        setRejectedData([]);
        setRejectedTotal(0);
        setRejectedError(responseData.statusDescription || 'No rejected bookings available.');
        updateToast(toastId, responseData.statusDescription || 'No rejected bookings available.', 'error');
      }
    } catch (error: any) {
      setRejectedData([]);
      setRejectedTotal(0);
      setRejectedError(error.response?.data?.statusDescription || 'Failed to fetch rejected bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch rejected bookings.', 'error');
    } finally {
      setIsRejectedLoading(false);
    }
  };

  const loadRejectedDetails = async () => {
    const toastId = showLoadingToast('Loading rejected booking details...');
    try {
      const countResponse = await apiDefinitions.getRejectedBookings(1);
      const countData = countResponse.data as ApiResponse;

      if (countData.status === 'S1000') {
        const OngoingBookings = countData.data.vehicleBookingRequests || [];
        const filteredRejected = OngoingBookings.filter((b: Booking) =>
          b.status === 'REJECTED' || b.status === 'FINAL_REJECTED'
        );

        setRejectedTotal(filteredRejected.length);
        setRejectedDetails(filteredRejected);
        setRejectedModalError(null);
        updateToast(toastId, 'Rejected booking details loaded successfully!', 'success');
      } else {
        setRejectedDetails([]);
        setRejectedTotal(0);
        setRejectedModalError(countData.statusDescription || 'Failed to retrieve rejected booking details.');
        updateToast(toastId, countData.statusDescription || 'Failed to retrieve rejected booking details.', 'error');
      }
    } catch (error: any) {
      setRejectedDetails([]);
      setRejectedTotal(0);
      setRejectedModalError(error.response?.data?.statusDescription || 'Failed to fetch rejected booking details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch rejected booking details.', 'error');
    }
  };

  const fetchRejectedDetails = async () => {
    await loadRejectedDetails();
    setShowRejectedModal(true);
  };

  const fetchRecurringData = async () => {
    if (isRecurringLoading) return;
    const toastId = showLoadingToast('Fetching recurring bookings...');
    setIsRecurringLoading(true);
    try {
      const response = await apiDefinitions.getRecurringBookings(1);
      const responseData = response.data as RecurringApiResponse;
      if (responseData.status === 'S1000' && responseData.data.bookings.length > 0) {
        setRecurringData(responseData.data.bookings);
        setRecurringTotal(responseData.data.total || responseData.data.bookings.length);
        setRecurringError(null);
        updateToast(toastId, 'Recurring bookings fetched successfully!', 'success');
      } else {
        setRecurringData([]);
        setRecurringTotal(0);
        setRecurringError('No recurring bookings available.');
        updateToast(toastId, 'No recurring bookings available.', 'error');
      }
    } catch (error: any) {
      setRecurringData([]);
      setRecurringTotal(0);
      setRecurringError(error.response?.data?.statusDescription || 'Failed to fetch recurring bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch recurring bookings.', 'error');
    } finally {
      setIsRecurringLoading(false);
    }
  };

  const loadRecurringDetails = async () => {
    const toastId = showLoadingToast('Loading recurring booking details...');
    try {
      const response = await apiDefinitions.getRecurringBookings(1);
      const responseData = response.data as RecurringApiResponse;
      if (responseData.status === 'S1000') {
        setRecurringDetails(responseData.data.bookings);
        setRecurringModalError(null);
        updateToast(toastId, 'Recurring booking details loaded successfully!', 'success');
      } else {
        setRecurringDetails([]);
        setRecurringModalError(responseData.statusDescription || 'Failed to retrieve recurring booking details.');
        updateToast(toastId, responseData.statusDescription || 'Failed to retrieve recurring booking details.', 'error');
      }
    } catch (error: any) {
      setRecurringDetails([]);
      setRecurringModalError(error.response?.data?.statusDescription || 'Failed to fetch recurring booking details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch recurring booking details.', 'error');
    }
  };

  const fetchRecurringDetails = async () => {
    await loadRecurringDetails();
    setShowRecurringModal(true);
  };

  const fetchMergeSuggestions = async () => {
    if (isMergeLoading) return;
    const toastId = showLoadingToast('Fetching merge suggestions...');
    setIsMergeLoading(true);
    try {
      const response = await apiDefinitions.getMergeSuggestions();
      const responseData = response.data as MergeApiResponse;
      if (responseData.status === 'S1000' && responseData.data.length > 0) {
        setMergeSuggestions(responseData.data);
        setMergeTotal(responseData.data.length);
        setMergeError(null);
        updateToast(toastId, 'Merge suggestions fetched successfully!', 'success');
      } else {
        setMergeSuggestions([]);
        setMergeTotal(0);
        setMergeError(responseData.statusDescription || 'No merge suggestions available.');
        updateToast(toastId, responseData.statusDescription || 'No merge suggestions available.', 'error');
      }
    } catch (error: any) {
      setMergeSuggestions([]);
      setMergeTotal(0);
      setMergeError(error.response?.data?.statusDescription || 'Failed to fetch merge suggestions.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch merge suggestions.', 'error');
    } finally {
      setIsMergeLoading(false);
    }
  };

  const loadMergeDetails = async () => {
    const toastId = showLoadingToast('Loading merge suggestion details...');
    try {
      const response = await apiDefinitions.getMergeSuggestions();
      const responseData = response.data as MergeApiResponse;
      if (responseData.status === 'S1000') {
        setMergeDetails(responseData.data);
        setMergeModalError(null);
        updateToast(toastId, 'Merge suggestion details loaded successfully!', 'success');
      } else {
        setMergeDetails([]);
        setMergeModalError(responseData.statusDescription || 'Failed to retrieve merge suggestion details.');
        updateToast(toastId, responseData.statusDescription || 'Failed to retrieve merge suggestion details.', 'error');
      }
    } catch (error: any) {
      setMergeDetails([]);
      setMergeModalError(error.response?.data?.statusDescription || 'Failed to fetch merge suggestion details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch merge suggestion details.', 'error');
    }
  };

  const fetchMergeDetails = async () => {
    await loadMergeDetails();
    setShowMergeModal(true);
  };

  const fetchMergedGroups = async (statusFilter: string) => {
    if (isMergedLoading) return;
    const toastId = showLoadingToast('Fetching merged bookings...');
    setIsMergedLoading(true);
    try {
      const response = await apiDefinitions.getAllMergedGroups(statusFilter);
      const responseData = response.data as MergedApiResponse;
      if (responseData.status === 'S1000' && responseData.data.length > 0) {
        setMergedGroups(responseData.data);
        setMergedTotal(responseData.data.length);
        setMergedError(null);
        updateToast(toastId, 'Merged bookings fetched successfully!', 'success');
      } else {
        setMergedGroups([]);
        setMergedTotal(0);
        setMergedError(responseData.statusDescription || 'No merged bookings available.');
        updateToast(toastId, responseData.statusDescription || 'No merged bookings available.', 'error');
      }
    } catch (error: any) {
      setMergedGroups([]);
      setMergedTotal(0);
      setMergedError(error.response?.data?.statusDescription || 'Failed to fetch merged bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch merged bookings.', 'error');
    } finally {
      setIsMergedLoading(false);
    }
  };

  const loadMergedDetails = async (statusFilter: string) => {
    const toastId = showLoadingToast('Loading merged booking details...');
    try {
      const response = await apiDefinitions.getAllMergedGroups(statusFilter);
      const responseData = response.data as MergedApiResponse;
      if (responseData.status === 'S1000') {
        setMergedDetails(responseData.data);
        setMergedModalError(null);
        updateToast(toastId, 'Merged booking details loaded successfully!', 'success');
      } else {
        setMergedDetails([]);
        setMergedModalError(responseData.statusDescription || 'Failed to retrieve merged booking details.');
        updateToast(toastId, responseData.statusDescription || 'Failed to retrieve merged booking details.', 'error');
      }
    } catch (error: any) {
      setMergedDetails([]);
      setMergedModalError(error.response?.data?.statusDescription || 'Failed to fetch merged booking details.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch merged booking details.', 'error');
    }
  };

  const fetchMergedDetails = async (statusFilter: string) => {
    await loadMergedDetails(statusFilter);
    setShowMergedModal(true);
  };

  const approveVehicleBooking = async (bookingId: number) => {
    if (!bookingId) {
      setApproveError('Booking ID is missing.');
      return false;
    }
    const toastId = showLoadingToast('Approving booking...');
    setIsApproving(true);
    setApproveError(null);
    try {
      const payload = {
        id: bookingId,
        status: 'APPROVED',
        comments: 'Approved by HOD',
      };
      const response = await apiDefinitions.approveVehicleBooking(payload);
      if (response.data.status === 'S1000') {
        await Promise.all([
          fetchPendingData(),
          fetchApprovedData(),
          fetchNewApprovedData(),
          fetchBookings(currentPage),
        ]);
        updateToast(toastId, 'Booking approved successfully!', 'success');
        Swal.fire({
          icon: 'success',
          title: 'Booking Approved',
          text: 'The booking has been successfully approved.',
          confirmButtonText: 'OK',
        });
        return true;
      } else {
        setApproveError(response.data.statusDescription || 'Failed to approve booking.');
        updateToast(toastId, response.data.statusDescription || 'Failed to approve booking.', 'error');
        Swal.fire({
          icon: 'error',
          title: 'Approval Failed',
          text: response.data.statusDescription || 'Failed to approve booking.',
          confirmButtonText: 'OK',
        });
        return false;
      }
    } catch (error: any) {
      setApproveError(error.response?.data?.statusDescription || 'Failed to approve booking.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to approve booking.', 'error');
      Swal.fire({
        icon: 'error',
        title: 'Approval Failed',
        text: error.response?.data?.statusDescription || 'Failed to approve booking.',
        confirmButtonText: 'OK',
      });
      return false;
    } finally {
      setIsApproving(false);
    }
  };

const finalApproveVehicleBooking = async (
  bookingId: number,
  driverId: string | null,
  vehicleId: string | null,
  comment: string,
  status: string
) => {
  // create a neutral loading toast up-front
  const toastId = showLoadingToast(`Processing ${status.toLowerCase().replace('_', ' ')}...`);

  if (!bookingId) {
    const msg = 'Booking ID is missing.';
    setApproveError(msg);
    updateToast(toastId, msg, 'error');
    return false;
  }
  if (status === 'FINAL_APPROVED' && (!driverId || !vehicleId)) {
    const msg = 'Driver and vehicle are required for final approval.';
    setApproveError(msg);
    updateToast(toastId, msg, 'error');
    return false;
  }

  setIsApproving(true);
  setApproveError(null);

  try {
    const payload = {
      id: bookingId,
      status,
      driverId: driverId || undefined,
      vehicleId: vehicleId || undefined,
      comment,
    };
    const response = await apiDefinitions.finalApproveVehicleBooking(payload);

    if (response.data.status === 'S1000') {
      await Promise.all([
        fetchPendingData(),
        fetchApprovedData(),
        fetchNewApprovedData(),
        fetchFinalApprovedData(),
        fetchRejectedData(),
        fetchBookings(currentPage),
      ]);
      updateToast(toastId, `Booking ${status.toLowerCase().replace('_', ' ')} successfully!`, 'success');
      Swal.fire({
        icon: 'success',
        title: `Booking ${status.replace('_', ' ')}`,
        text: `The booking has been successfully ${status.toLowerCase().replace('_', ' ')}.`,
      });
      return true;
    } else {
      const msg = response.data.statusDescription || 'Failed to update booking.';
      setApproveError(msg);
      updateToast(toastId, msg, 'error');
      Swal.fire({ icon: 'error', title: `${status.replace('_', ' ')} Failed`, text: msg });
      return false;
    }
  } catch (error: any) {
    const msg =
      error.response?.data?.statusDescription ||
      `Failed to ${status.toLowerCase().replace('_', ' ')} booking.`;
    setApproveError(msg);
    updateToast(toastId, msg, 'error');
    Swal.fire({ icon: 'error', title: `${status.replace('_', ' ')} Failed`, text: msg });
    return false;
  } finally {
    setIsApproving(false);
  }
};


  const updateVehicleBooking = async (bookingId: number, updatedFields: Partial<Booking>) => {
  const toastId = showLoadingToast('Updating booking...');

  if (!bookingId) {
    const msg = 'Booking ID is missing.';
    setUpdateError(msg);
    updateToast(toastId, msg, 'error');
    Swal.fire({ icon: 'error', title: 'Update Failed', text: msg, confirmButtonText: 'OK' });
    return false;
  }

  setIsUpdating(true);
  setUpdateError(null);
  try {
    const payload = { id: bookingId, ...updatedFields };
    const response = await apiDefinitions.updateVehicleBooking(payload);

    if (response.data.status === 'S1000') {
      await Promise.all([
        fetchBookings(currentPage),
        fetchPendingData(),
        fetchApprovedData(),
        fetchNewApprovedData(),
        fetchFinalApprovedData(),
        fetchCompletedData(),
        fetchRejectedData(),
        fetchOngoingBookingsData(),
        fetchRecurringData(),
        fetchMergeSuggestions(),
        fetchMergedGroups(mergedFilter),
      ]);
      updateToast(toastId, 'Booking updated successfully!', 'success');
      Swal.fire({ icon: 'success', title: 'Booking Updated', text: 'The booking has been successfully updated.' });
      return true;
    } else {
      const msg = response.data.statusDescription || 'Failed to update booking.';
      setUpdateError(msg);
      updateToast(toastId, msg, 'error');
      Swal.fire({ icon: 'error', title: 'Update Failed', text: msg, confirmButtonText: 'OK' });
      return false;
    }
  } catch (error: any) {
    const msg = error.response?.data?.statusDescription || 'Failed to update booking.';
    setUpdateError(msg);
    updateToast(toastId, msg, 'error');
    Swal.fire({ icon: 'error', title: 'Update Failed', text: msg, confirmButtonText: 'OK' });
    return false;
  } finally {
    setIsUpdating(false);
  }
};


  const acceptMergeGroups = async (groupId: string, numberPlate: string | null, empNumber: string | null) => {
  const toastId = showLoadingToast('Accepting merge group...');

  if (!groupId || !numberPlate || !empNumber) {
    const msg = 'Group ID, vehicle, or driver is missing.';
    setMergeActionError(msg);
    updateToast(toastId, msg, 'error');
    Swal.fire({ icon: 'error', title: 'Merge Failed', text: msg, confirmButtonText: 'OK' });
    return false;
  }

  setIsMerging(true);
  setMergeActionError(null);
  try {
    const mergeGroup = mergeSuggestions.find((group) => group.groupId === groupId);
    const bookingIds = mergeGroup ? mergeGroup.bookings.map((b) => Number(b.id)) : [];
    if (bookingIds.length === 0) {
      const msg = 'No bookings found in the merge group.';
      setMergeActionError(msg);
      updateToast(toastId, msg, 'error');
      Swal.fire({ icon: 'error', title: 'Merge Failed', text: msg, confirmButtonText: 'OK' });
      return false;
    }

    const payload = { action: 'ACCEPT', bookingIds, numberPlate, empNumber };
    const response = await apiDefinitions.acceptMergeGroups(groupId, payload);

    if (response.data.status === 'S1000') {
      await Promise.all([fetchMergeSuggestions(), fetchMergedGroups(mergedFilter)]);
      updateToast(toastId, 'Merge group accepted successfully!', 'success');
      Swal.fire({ icon: 'success', title: 'Merge Accepted', text: 'The merge group has been successfully accepted.' });
      return true;
    } else {
      const msg = response.data.statusDescription || 'Failed to accept merge group.';
      setMergeActionError(msg);
      updateToast(toastId, msg, 'error');
      Swal.fire({ icon: 'error', title: 'Merge Failed', text: msg, confirmButtonText: 'OK' });
      return false;
    }
  } catch (error: any) {
    const msg = error.response?.data?.statusDescription || 'Failed to accept merge group.';
    setMergeActionError(msg);
    updateToast(toastId, msg, 'error');
    Swal.fire({ icon: 'error', title: 'Merge Failed', text: msg, confirmButtonText: 'OK' });
    return false;
  } finally {
    setIsMerging(false);
  }
};


  const rejectMergeGroups = async (groupId: string, comment: string) => {
  const toastId = showLoadingToast('Rejecting merge group...');

  if (!groupId) {
    const msg = 'Group ID is missing.';
    setMergeActionError(msg);
    updateToast(toastId, msg, 'error');
    Swal.fire({ icon: 'error', title: 'Rejection Failed', text: msg, confirmButtonText: 'OK' });
    return false;
  }

  setIsMerging(true);
  setMergeActionError(null);
  try {
    const mergeGroup = mergeSuggestions.find((group) => group.groupId === groupId);
    if (!mergeGroup || !Array.isArray(mergeGroup.bookings) || mergeGroup.bookings.length < 2) {
      const msg = 'Merge group not found or does not have at least two bookings.';
      setMergeActionError(msg);
      updateToast(toastId, msg, 'error');
      Swal.fire({ icon: 'error', title: 'Rejection Failed', text: msg, confirmButtonText: 'OK' });
      return false;
    }

    const bookingIds = mergeGroup.bookings.map((b) => Number(b.id)).filter((id) => !isNaN(id));
    if (bookingIds.length < 2) {
      const msg = 'At least two valid booking IDs are required.';
      setMergeActionError(msg);
      updateToast(toastId, msg, 'error');
      Swal.fire({ icon: 'error', title: 'Rejection Failed', text: msg, confirmButtonText: 'OK' });
      return false;
    }

    const payload = { action: 'REJECT', comment, bookingIds, numberPlate: null, empNumber: null, departureTime: null };
    const response = await apiDefinitions.rejectMergeGroups(groupId, payload);

    if (response.data.status === 'S1000') {
      await Promise.all([fetchMergeSuggestions(), fetchMergedGroups(mergedFilter)]);
      updateToast(toastId, 'Merge group rejected successfully!', 'success');
      Swal.fire({ icon: 'success', title: 'Merge Rejected', text: 'The merge group has been successfully rejected.' });
      return true;
    } else {
      const msg = response.data.statusDescription || 'Failed to reject merge group.';
      setMergeActionError(msg);
      updateToast(toastId, msg, 'error');
      Swal.fire({ icon: 'error', title: 'Rejection Failed', text: msg, confirmButtonText: 'OK' });
      return false;
    }
  } catch (error: any) {
    const msg = error.response?.data?.statusDescription || 'Failed to reject merge group.';
    setMergeActionError(msg);
    updateToast(toastId, msg, 'error');
    Swal.fire({ icon: 'error', title: 'Rejection Failed', text: msg, confirmButtonText: 'OK' });
    return false;
  } finally {
    setIsMerging(false);
  }
};


  const handleApproveClick = async (bookingId: number) => {
    if (isApproving || !checkPermission('ROLE_APPROVE_VEHICLE_BOOKING')) return;
    const success = await approveVehicleBooking(bookingId);
    if (success && showPendingModal) {
      await loadPendingDetails();
    }
  };

  const handleFinalApprove = async (
    bookingId: number,
    driverId: string | null,
    vehicleId: string | null,
    comment: string,
    status: string
  ): Promise<boolean> => {
    const success = await finalApproveVehicleBooking(
      bookingId,
      driverId,
      vehicleId,
      comment,
      status
    );

    if (success && showFinalApprovedModal) await loadFinalApprovedDetails();
    if (success && status === 'FINAL_REJECTED' && showRejectedModal) await loadRejectedDetails();
    return success;
  };

  const handleEditClick = (booking: Booking) => {
    if (isUpdating) {
      return;
    }
    const isCreator = booking.createdBy === currentUser.empNumber;
    const isServiceManager = currentUser.roles.includes('ROLE_SERVICE_MANAGER') || currentUser.group === 'SERVICE_MANAGER';

    setSelectedBooking(booking);
    setShowEditModal(true);
  };

  const handleClosePendingModal = () => {
    setShowPendingModal(false);
    setPendingDetails([]);
    setPendingModalError(null);
    setApproveError(null);
  };

  const handleCloseApprovedModal = () => {
    setShowApprovedModal(false);
    setApprovedDetails([]);
    setApprovedModalError(null);
  };

  const handleCloseNewApprovedModal = () => {
    setShowNewApprovedModal(false);
    setNewApprovedDetails([]);
    setNewApprovedModalError(null);
  };

  const handleCloseFinalApprovedModal = () => {
    setShowFinalApprovedModal(false);
    setFinalApprovedDetails([]);
    setFinalApprovedModalError(null);
  };

  const handleCloseCompletedModal = () => {
    setShowCompletedModal(false);
    setCompletedDetails([]);
    setCompletedModalError(null);
  };

  const handleCloseRejectedModal = () => {
    setShowRejectedModal(false);
    setRejectedDetails([]);
    setRejectedModalError(null);
  };

  const handleCloseOngoingBookingsModal = () => {
    setShowOngoingBookingsModal(false);
    setOngoingBookingsDetails([]);
    setOngoingBookingsModalError(null);
  };

  const handleCloseRecurringModal = () => {
    setShowRecurringModal(false);
    setRecurringDetails([]);
    setRecurringModalError(null);
  };

  const handleCloseMergeModal = () => {
    setShowMergeModal(false);
    setMergeDetails([]);
    setMergeModalError(null);
  };

  const handleCloseMergedModal = () => {
    setShowMergedModal(false);
    setMergedDetails([]);
    setMergedModalError(null);
    setMergedFilter('ACCEPTED');
  };

  const handleCloseEditModal = async () => {
    setShowEditModal(false);
    setSelectedBooking(null);
    await fetchBookings(currentPage);
  };

  const fetchBookings = async (page: number | 'last') => {
    const toastId = showLoadingToast('Fetching your bookings...');
    setLoading(true);
    try {
      let pageNumber = typeof page === 'number' ? page : 1;
      const payload = { filterValue: '' };
      if (page === 'last') {
        const countResponse = await apiDefinitions.getUserCreatedBookings(1, payload);
        const countData = countResponse.data as ApiResponse;
        if (countData.status === 'S1000') {
          const total = countData.data.total || countData.data.vehicleBookingRequests.length;
          pageNumber = Math.ceil(total / pageSize);
        } else {
          setBookings([]);
          setTotalRecords(0);
          setError(countData.statusDescription || 'No bookings found.');
          updateToast(toastId, countData.statusDescription || 'No bookings found.', 'error');
          setLoading(false);
          return;
        }
      }
      const response = await apiDefinitions.getUserCreatedBookings(pageNumber, payload);
      const responseData = response.data as ApiResponse;
      if (responseData.status === 'S1000' && responseData.data.vehicleBookingRequests) {
        const sortedBookings = responseData.data.vehicleBookingRequests
          .map((booking) => ({
            ...booking,
            createdBy: booking.createdBy || 'unknown',
            status: booking.status || 'UNKNOWN',
            intermediateApprovedBy: booking.intermediateApprovedBy || null,
          }))
          .sort((a, b) => b.id - a.id);
        setBookings(sortedBookings);
        setTotalRecords(responseData.data.total || responseData.data.vehicleBookingRequests.length);
        setCurrentPage(pageNumber);
        setError(null);
        updateToast(toastId, 'Your bookings fetched successfully!', 'success');
      } else {
        setBookings([]);
        setTotalRecords(0);
        setError(responseData.statusDescription || 'No bookings found.');
        updateToast(toastId, responseData.statusDescription || 'No bookings found.', 'error');
      }
    } catch (error: any) {
      setBookings([]);
      setTotalRecords(0);
      setError(error.response?.data?.statusDescription || 'Failed to fetch bookings.');
      updateToast(toastId, error.response?.data?.statusDescription || 'Failed to fetch bookings.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(1);
    if (checkPermission('ROLE_PENDING_VEHICLE_BOOKING')) fetchPendingData();
    if (checkPermission('ROLE_APPROVE_VEHICLE_BOOKING')) {
      fetchApprovedData();
      fetchNewApprovedData();
    }
    if (checkPermission('ROLE_FINAL_APPROVE_VEHICLE_BOOKING')) fetchFinalApprovedData();
    if (checkPermission('ROLE_COMPLETED_VEHICLE_BOOKING')) fetchCompletedData();
    if (checkPermission('ROLE_VIEW_REJECTED_BOOKINGS')) fetchRejectedData();
    if (checkPermission('ROLE_VIEW_REJECTED_BOOKINGS')) fetchOngoingBookingsData();
    if (checkPermission('ROLE_GET_RECURRING_VEHICLE_BOOKING_REQUESTS')) fetchRecurringData();
    if (checkPermission('ROLE_GET_SUGGESTIONS_FOR_MERGE_VEHICLE_BOOKING')) fetchMergeSuggestions();
    if (checkPermission('ROLE_GET_MERGE_GROUPS')) fetchMergedGroups(mergedFilter);
  }, [mergedFilter]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= Math.ceil(totalRecords / pageSize)) {
      setCurrentPage(page);
      fetchBookings(page);
    }
  };

  const handleViewClick = (booking: Booking) => {
    if (booking?.id) {
      setSelectedBooking(booking);
      setShowViewModal(true);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">
                    <i className="ti ti-dashboard" style={{ color: "var(--rm-primary)" }} />
                    <span>Booking Dashboard</span>
                  </h4>
                  <div className="text-muted small mt-1">Vehicle Management &bull; Booking &bull; Operational Hub</div>
                </div>
                <div className="col-4 text-end d-flex align-items-center justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-primary d-inline-flex align-items-center gap-1 shadow-sm"
                    style={{ borderRadius: '8px', fontWeight: 600, padding: '7px 14px' }}
                    onClick={() => setShowGatePassModal(true)}
                  >
                    <i className="ti ti-scan" style={{ fontSize: '1.1rem' }}></i>
                    <span>Gate Pass (IN / OUT)</span>
                  </button>
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <ul className="nav nav-tabs">
                {navItems1.map((item, index) =>
                  item.permission && checkPermission(item.permission) ? (
                    <li className="nav-item" key={index}>
                      <Link to={item.link} className={`nav-link ${index === 0 ? 'active' : ''}`}>
                        <i className={item.icon}></i> {item.label}
                      </Link>
                    </li>
                  ) : null
                )}
              </ul>
            </div>
            <div className="row mt-4" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
              {(['PENDING', 'FINAL_APPROVED_NEW', 'FINAL_APPROVED', 'APPROVED', 'COMPLETED', 'REJECTED', 'OngoingBookings', 'RECURRING', 'MERGE_SUGGESTIONS', 'MERGED_BOOKINGS'] as const).map((status) =>
                checkPermission(statusPermissions[status]) ? (
                  <div className="col-xl-3 col-sm-6 col-12 mb-4" style={{ padding: '0 15px' }} key={status}>
                    <div
                      className="stat-card bg-white rounded-xl shadow-md overflow-hidden"
                      style={{
                        transition: 'all 0.3s ease',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                      }}
                      onClick={() => {
                        if (status === 'OngoingBookings') fetchOngoingBookingsDetails();
                        else if (status === 'PENDING') fetchPendingDetails();
                        else if (status === 'APPROVED') fetchApprovedDetails();
                        else if (status === 'FINAL_APPROVED_NEW') fetchNewApprovedDetails();
                        else if (status === 'FINAL_APPROVED') fetchFinalApprovedDetails();
                        else if (status === 'COMPLETED') fetchCompletedDetails();
                        else if (status === 'REJECTED') fetchRejectedDetails();
                        else if (status === 'RECURRING') fetchRecurringDetails();
                        else if (status === 'MERGE_SUGGESTIONS') fetchMergeDetails();
                        else if (status === 'MERGED_BOOKINGS') fetchMergedDetails(mergedFilter);
                      }}
                    >
                      <div className="p-6" style={{ padding: '1.5rem' }}>
                        <div
                          className="flex items-center justify-between mb-4"
                          style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                          <div
                            className={`icon-container ${statusCardStyles[status]?.className || 'bg-gray-100'}`}
                            style={{
                              height: '60px',
                              width: '60px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '12px',
                              backgroundColor: statusCardStyles[status]?.bgColor || '#f3f4f6',
                            }}
                          >
                            <i
                              className={statusCardStyles[status]?.icon || 'fas fa-info-circle'}
                              style={{ fontSize: '1.5rem', color: statusCardStyles[status]?.iconColor || '#6b7280' }}
                            ></i>
                          </div>
                          <span
                            style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}
                          >
                            {status === 'OngoingBookings'
                              ? OngoingBookingsTotal
                              : status === 'PENDING'
                              ? pendingTotal
                              : status === 'APPROVED'
                              ? approvedTotal
                              : status === 'FINAL_APPROVED_NEW'
                              ? newApprovedTotal
                              : status === 'FINAL_APPROVED'
                              ? finalApprovedTotal
                              : status === 'COMPLETED'
                              ? completedTotal
                              : status === 'REJECTED'
                              ? rejectedTotal
                              : status === 'RECURRING'
                              ? recurringTotal
                              : status === 'MERGE_SUGGESTIONS'
                              ? mergeTotal
                              : mergedTotal}
                          </span>
                        </div>
                        <h3
                          style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: '0' }}
                        >
                                                 {statusLabels[status] || status}
                                               </h3>
                                             </div>
                                           </div>
                                         </div>
                                       ) : null
                                     )}
                                   </div>
                                   <div className="row">
                                     <div className="col-md-12">
                                       <div className="card">
                                         <div className="card-header d-flex justify-content-between">
                                           <div className="card-title">
                                             My Booking List (Page {currentPage} of {Math.ceil(totalRecords / pageSize) || 1})
                                           </div>
                                         </div>
                                         <div className="card-body">
                                           {loading && <div className="text-center">Loading...</div>}
                                           {error && <div className="alert-error">{error}</div>}
                                           {updateError && <div className="alert-error">{updateError}</div>}
                                           {mergeActionError && <div className="alert-error">{mergeActionError}</div>}
                                           {approveError && <div className="alert-error">{approveError}</div>}
                                           {!loading && !error && bookings.length === 0 && (
                                             <div className="alert-info">No bookings found.</div>
                                           )}
                                           {!loading && !error && bookings.length > 0 && (
                                             <div className="table-responsive">
                                               <table className="table text-nowrap">
                                                 <thead className="table-primary">
                                                   <tr>
                                                     <th>ID</th>
                                                     <th>Vehicle</th>
                                                     <th>Driver</th>
                                                     <th>Status</th>
                                                     <th>Departure Time</th>
                                                     <th>Priority</th>
                                                     <th>Actions</th>
                                                   </tr>
                                                 </thead>
                                                 <tbody>
                                                   {bookings.map((booking, index) => (
                                                     <tr key={index}>
                                                        <td>{booking.id}</td>
                                                       <td>
                                                         <span className={`badge ${booking.vehicle?.numberPlate ? 'bg-success' : 'bg-secondary'}`}>
                                                           {booking.vehicle?.numberPlate || 'Not Assigned'}
                                                         </span>
                                                       </td> 
                                                      <td>
                                                        <span
                                                          className={`badge ${booking.driver?.name || booking.driverId ? 'bg-success' : 'bg-secondary'}`}
                                                          style={{ minWidth: '120px', display: 'inline-block' }}
                                                        >
                                                          {booking.driver?.name
                                                            ? booking.driver.name.split(' ').slice(-3).join(' ')
                                                            : booking.driverId || 'Not Assigned'}
                                                        </span>
                                                        
                                                      </td>
                                                       <td>
                                                         <span className={statusColors[booking.status] || 'badge bg-secondary'}>
                                                           {booking.status || 'N/A'}
                                                         </span>
                                                       </td>
                                                       <td>
                                                         {booking.departureTime
                                                           ? new Date(Number(booking.departureTime)).toLocaleString()
                                                           : 'N/A'}
                                                       </td>
                                                       <td>{booking.priority || 'N/A'}</td>
                                                       <td>
                                                         <div className="hstack gap-2 fs-3">
                                                           <button
                                                             className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                                                             onClick={() => handleViewClick(booking)}
                                                             title="View Booking"
                                                           >
                                                             <i className="feather-eye" />
                                                           </button>
                                                           {canUpdateBooking(booking) && (
                                                             <button
                                                               className="btn-icon btn-sm btn-soft-info rounded-pill"
                                                               onClick={() => handleEditClick(booking)}
                                                               title="Edit Booking"
                                                             >
                                                               <i className="feather-edit" />
                                                             </button>
                                                           )}
                                                         </div>
                                                       </td>
                                                     </tr>
                                                   ))}
                                                 </tbody>
                                               </table>
                                             </div>
                                           )}
                                           <div className="d-flex justify-content-between mt-3">
                                             <Button
                                               variant="outline-primary"
                                               disabled={currentPage === 1}
                                               onClick={() => handlePageChange(currentPage - 1)}
                                             >
                                               Previous
                                             </Button>
                                             <span>Page {currentPage} of {Math.ceil(totalRecords / pageSize) || 1}</span>
                                             <Button
                                               variant="outline-warning"
                                               disabled={currentPage >= Math.ceil(totalRecords / pageSize)}
                                               onClick={() => handlePageChange(currentPage + 1)}
                                             >
                                               Next
                                             </Button>
                                           </div>
                                         </div>
                                       </div>
                                     </div>
                                   </div>
                                   {selectedBooking && (
                                     <ViewVehicleBookings
                                       show={showViewModal}
                                       onHide={() => {
                                         setShowViewModal(false);
                                         setSelectedBooking(null);
                                       }}
                                       booking={selectedBooking}
                                       statusColors={statusColors}
                                     />
                                   )}
                                   {selectedBooking && (
                                     <EditVehicleBookings
                                       show={showEditModal}
                                       onHide={handleCloseEditModal}
                                       booking={selectedBooking}
                                       updateVehicleBooking={updateVehicleBooking}
                                       isUpdating={isUpdating}
                                     />
                                   )}
                                   <PendingBookingsModal
                                     show={showPendingModal}
                                     onHide={handleClosePendingModal}
                                     pendingDetails={pendingDetails}
                                     pendingModalError={pendingModalError}
                                     approveError={approveError}
                                     isApproving={isApproving}
                                     handleViewClick={handleViewClick}
                                     handleApproveClick={handleApproveClick}
                                     statusColors={statusColors}
                                   />
                                   <ApprovedBookingsModal
                                     show={showApprovedModal}
                                     onHide={handleCloseApprovedModal}
                                     approvedDetails={approvedDetails}
                                     approvedModalError={approvedModalError}
                                     statusColors={statusColors}
                                     handleViewClick={handleViewClick}
                                   />
                                   <ApprovedModal
                                     show={showNewApprovedModal}
                                     onHide={handleCloseNewApprovedModal}
                                     approvedDetails={newApprovedDetails}
                                     approvedModalError={newApprovedModalError}
                                     statusColors={statusColors}
                                     handleViewClick={handleViewClick}
                                     handleEditClick={handleEditClick}
                                   />
                                   <FinalApprovedBookingsModal
                                     show={showFinalApprovedModal}
                                     onHide={handleCloseFinalApprovedModal}
                                     finalApprovedDetails={finalApprovedDetails}
                                     finalApprovedModalError={finalApprovedModalError}
                                     statusColors={statusColors}
                                     handleFinalApprove={handleFinalApprove}
                                   />
                                   <CompletedBookingsModal
                                     show={showCompletedModal}
                                     onHide={handleCloseCompletedModal}
                                     completedDetails={completedDetails}
                                     completedModalError={completedModalError}
                                     statusColors={statusColors}
                                   />
                                   <RejectedBookingsModal
                                     show={showRejectedModal}
                                     onHide={handleCloseRejectedModal}
                                     rejectedDetails={rejectedDetails}
                                     rejectedModalError={rejectedModalError}
                                     handleViewClick={handleViewClick}
                                     statusColors={statusColors}
                                   />
                                   <OngoingBookingsModal
                                     show={showOngoingBookingsModal}
                                     onHide={handleCloseOngoingBookingsModal}
                                     OngoingBookingsDetails={OngoingBookingsDetails}
                                     OngoingBookingsModalError={OngoingBookingsModalError}
                                     handleViewClick={handleViewClick}
                                     handleEditClick={handleEditClick}
                                     statusColors={statusColors}
                                   />
                                   <RecurringBookingsModal
                                     show={showRecurringModal}
                                     onHide={handleCloseRecurringModal}
                                     recurringDetails={recurringDetails}
                                     recurringModalError={recurringModalError}
                                     statusColors={statusColors}
                                     setRecurringDetails={setRecurringDetails}
                                   />
                                   <MergeRequestModal
                                     show={showMergeModal}
                                     onHide={handleCloseMergeModal}
                                     mergeDetails={mergeDetails}
                                     mergeModalError={mergeModalError}
                                     handleViewClick={handleViewClick}
                                     handleAcceptMerge={acceptMergeGroups}
                                     handleRejectMerge={rejectMergeGroups}
                                     statusColors={statusColors}
                                     isMerging={isMerging}
                                   />
                                   <MergedBookingsModal
                                     show={showMergedModal}
                                     onHide={handleCloseMergedModal}
                                     mergedDetails={mergedDetails}
                                     mergedModalError={mergedModalError}
                                     statusColors={statusColors}
                                     mergedFilter={mergedFilter}
                                     setMergedFilter={setMergedFilter}
                                     fetchMergedDetails={fetchMergedDetails}
                                   />
                                   <DirectGatePassModal
                                     show={showGatePassModal}
                                     onHide={() => setShowGatePassModal(false)}
                                     onSuccess={() => {
                                       fetchBookings(currentPage);
                                       if (checkPermission('ROLE_APPROVE_VEHICLE_BOOKING')) fetchApprovedData();
                                       if (checkPermission('ROLE_FINAL_APPROVE_VEHICLE_BOOKING')) fetchFinalApprovedData();
                                       if (checkPermission('ROLE_VIEW_REJECTED_BOOKINGS')) fetchOngoingBookingsData();
                                       if (checkPermission('ROLE_COMPLETED_VEHICLE_BOOKING')) fetchCompletedData();
                                     }}
                                   />
                                 </div>
                               </div>
                             </div>
                           </div>
                         );
                       };
                       
                       export default Booking;